/**
 * <shipping-cutoff> — counts down to the dispatch cutoff, in the warehouse's
 * timezone rather than the visitor's.
 *
 * Loaded with `defer`, which matters more than it looks. This element renders
 * more than once per page (the product block and the cart drawer), and an
 * inline definition next to the markup upgrades the *second* instance while
 * the parser is still inside its opening tag — `textContent` is empty at that
 * moment, so the countdown gets written first and the server-rendered sentence
 * is appended after it. That shipped once and read as two sentences run
 * together. Deferring means every instance is parsed before any upgrade runs,
 * and the fallback is read from an attribute rather than from child text, so
 * the timing cannot matter again.
 */
(() => {
  if (customElements.get('shipping-cutoff')) {
    return;
  }

  customElements.define(
    'shipping-cutoff',
    class extends HTMLElement {
      connectedCallback() {
        this.update();
        this.timer = setInterval(() => this.update(), 30000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      update() {
        // Whatever the server rendered is the honest answer for every state
        // that isn't "counting down".
        const fallback = this.getAttribute('data-fallback') || '';

        if (this.hasAttribute('local-time')) {
          try {
            this.updateLocal();
          } catch (error) {
            this.textContent = fallback;
          }
          return;
        }

        try {
          const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: this.getAttribute('time-zone'),
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23'
          }).formatToParts(new Date());

          const at = (type) => parts.find((part) => part.type === type).value;
          const weekday = at('weekday');
          const cutoffHour = parseInt(this.getAttribute('cutoff-hour'), 10);
          const minutesLeft = cutoffHour * 60 - (parseInt(at('hour'), 10) * 60 + parseInt(at('minute'), 10));

          // Weekends and Friday-after-cutoff get the plain sentence: it
          // already says "before 3PM on weekdays", which stays true. The
          // "ship tomorrow" line is Mon-Thu after the cutoff only -- after
          // Friday's cutoff the next dispatch is Monday, so "tomorrow"
          // would be a lie precisely when it sounds most reassuring.
          if (weekday === 'Sat' || weekday === 'Sun') {
            this.textContent = fallback;
            return;
          }

          if (minutesLeft <= 0) {
            const tomorrow = this.getAttribute('data-template-tomorrow');
            this.textContent = weekday !== 'Fri' && tomorrow ? tomorrow : fallback;
            return;
          }

          const hours = Math.floor(minutesLeft / 60);
          const minutes = minutesLeft % 60;

          this.textContent =
            hours > 0
              ? this.getAttribute('data-template-hm').replace('%H%', hours).replace('%M%', minutes)
              : this.getAttribute('data-template-m').replace('%M%', minutes);
        } catch (error) {
          // A bad timezone or a missing Intl implementation must never cost us
          // the sentence entirely.
          this.textContent = fallback;
        }
      }

      /**
       * US mode (`local-time`): the next cutoff as a moment in time, said in
       * the visitor's own clock. The countdown runs only while that moment is
       * on the visitor's own today, so "we ship today" means their today; any
       * later cutoff is named instead ("by 10:00 AM EDT tomorrow"). Weekends in
       * the warehouse's zone are skipped, so after Friday's cutoff it names
       * Monday.
       */
      updateLocal() {
        const zone = this.getAttribute('time-zone');
        const cutoffHour = parseInt(this.getAttribute('cutoff-hour'), 10);
        const now = new Date();

        const zoneParts = (date) => {
          const parts = {};
          new Intl.DateTimeFormat('en-US', {
            timeZone: zone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
          }).formatToParts(date).forEach((part) => { parts[part.type] = part.value; });
          return parts;
        };
        // The zone's offset from UTC at a given instant, in milliseconds.
        const offsetAt = (date) => {
          const p = zoneParts(date);
          return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - Math.floor(date.getTime() / 1000) * 1000;
        };

        const today = zoneParts(now);
        let cutoff = null;
        for (let i = 0; i < 8 && !cutoff; i++) {
          const wall = Date.UTC(+today.year, +today.month - 1, +today.day + i, cutoffHour, 0, 0);
          const instant = new Date(wall - offsetAt(new Date(wall)));
          const weekday = zoneParts(instant).weekday;
          if (weekday !== 'Sat' && weekday !== 'Sun' && instant > now) {
            cutoff = instant;
          }
        }
        if (!cutoff) {
          throw new Error('no cutoff');
        }

        const startOfLocalDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const daysAway = Math.round((startOfLocalDay(cutoff) - startOfLocalDay(now)) / 86400000);

        if (daysAway === 0) {
          const minutesLeft = Math.max(1, Math.floor((cutoff - now) / 60000));
          const hours = Math.floor(minutesLeft / 60);
          const minutes = minutesLeft % 60;
          this.textContent =
            hours > 0
              ? this.getAttribute('data-template-hm').replace('%H%', hours).replace('%M%', minutes)
              : this.getAttribute('data-template-m').replace('%M%', minutes);
          return;
        }

        const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(cutoff);
        this.textContent =
          daysAway === 1
            ? this.getAttribute('data-template-local-tomorrow').replace('%T%', time)
            : this.getAttribute('data-template-local-day')
                .replace('%T%', time)
                .replace('%D%', new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(cutoff));
      }
    }
  );
})();
